/**
 * Background auth user hydration over HTTPS — never blocks boot or BATON.
 * On failure, callers keep cached localStorage / auth.json identity.
 */
import { API_BASE } from './environment';
import { ensureFreshAccessToken } from '../services/api';

export const AUTH_HYDRATE_POLICY = {
  MAX_ATTEMPTS: 3,
  TIMEOUT_MS: 8000,
  BACKOFF_MS: [0, 400, 1200] as const,
} as const;

export type AuthUserPayload = {
  user?: { id?: string; email?: string };
  adminRole?: string | null;
  adminPermissions?: unknown;
};

export type HydrateAuthUserOptions = {
  getBearerToken: () => string | null;
  onPayload: (data: AuthUserPayload) => void;
  source?: string;
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Bounded HTTPS hydrate — returns true when /api/auth/user succeeded. */
export async function hydrateAuthUserWithRetry(
  opts: HydrateAuthUserOptions,
): Promise<boolean> {
  const source = opts.source || 'hydrate';

  for (let attempt = 0; attempt < AUTH_HYDRATE_POLICY.MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await sleep(AUTH_HYDRATE_POLICY.BACKOFF_MS[attempt] ?? 1200);
    }

    try {
      const fresh = await ensureFreshAccessToken().catch(() => null);
      const bearer = fresh || opts.getBearerToken();
      if (!bearer) return false;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), AUTH_HYDRATE_POLICY.TIMEOUT_MS);
      const res = await fetch(`${API_BASE}/api/auth/user`, {
        headers: { Authorization: `Bearer ${bearer}` },
        signal: controller.signal,
      }).finally(() => clearTimeout(timer));

      if (!res.ok) continue;

      const data = (await res.json()) as AuthUserPayload;
      opts.onPayload(data);
      return !!(data?.adminRole || data?.user?.email);
    } catch {
      /* offline / timeout — retry with backoff */
    }
  }

  console.info(`[Auth:hydrate] using cached identity after ${AUTH_HYDRATE_POLICY.MAX_ATTEMPTS} attempt(s) source=${source}`);
  return false;
}
